import React, { useEffect, useState } from "react";
import { Button, Form, Image, Input, message } from "antd";
import { authSVC } from "../../services";
import { useB2BContext } from "../../context/ContextB2B";
import AddressPopup from "../subscription_agreement/Address";
import "./Profile.scss";
import { AppModal, AppUpload } from "../../components/common";

function Profile() {
  const { getUserByID, getUserById } = useB2BContext();
  const [uploadedImage, setUploadedImage] = useState<any>(null);
  const [editContent, setEditContent] = useState<any>({
    id: "", username: "", full_name: "", company: "", email: "", image: "",
  });
  const [password, setPassword] = useState("");
  const [isEditable, setEditable] = useState<boolean>(true);
  const [visible, setIsVisible] = useState(false);
  const [isVisibleAddress, setIsVisibleAddress] = useState(false);
  const [isLoading, setisLoading] = useState<boolean>(false);

  useEffect(() => {
    setEditContent({
      id: getUserByID?._id,
      username: getUserByID?.username,
      full_name: getUserByID?.full_name,
      company: getUserByID?.company,
      email: getUserByID?.email,
      image: getUserByID?.cover_image,
    });
  }, [getUserByID]);

  const updateUser = () => {
    setisLoading(true);
    const formData: any = new FormData();
    formData.append("full_name", editContent?.full_name);
    formData.append("username", editContent?.username);
    formData.append("company", editContent?.company);
    formData.append("image", editContent?.image);
    authSVC.updateByUID(getUserByID?._id, formData)
      .then(() => {
        setisLoading(false);
        message.success("Profile Updated Successfully");
        getUserById(getUserByID?._id);
        setEditable(true);
      })
      .catch(() => setisLoading(false));
  };

  const updatePassword = () => {
    setisLoading(true);
    authSVC.changePassword({ password })
      .then(() => {
        setisLoading(false);
        message.success("Password Updated Successfully");
        setIsVisible(false);
        setEditable(true);
        getUserById(getUserByID?._id);
        handleCancel();
      })
      .catch(() => setisLoading(false));
  };

  const handleFileUpload = async (event: any) => {
    event.preventDefault();
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      message.error("Image size should not exceed 10MB");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setUploadedImage(reader.result);
    setEditContent((prev: any) => ({ ...prev, image: file }));
    reader.readAsDataURL(file);
    setEditable(false);
  };

  const handleChangeInput = (e: any) => {
    setEditContent((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
    setEditable(false);
  };

  const handleCancel = () => setIsVisible(false);
  const handleCancelAddress = () => setIsVisibleAddress(false);

  const onFinish = () => {
    if (editContent.full_name === "") {
      message.info("Please input full name"); setEditable(true);
    } else if (editContent.username === "") {
      message.info("Please input username"); setEditable(true);
    } else if (editContent.image === "") {
      message.info("Please upload an image"); setEditable(true);
    } else {
      setEditable(false);
      updateUser();
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-card">

        {/* ── Magenta banner ─────────────────────────────────── */}
        <div className="profile-card__banner">
          <div className="profile-card__banner-blobs" aria-hidden="true">
            <span className="profile-card__blob profile-card__blob--1" />
            <span className="profile-card__blob profile-card__blob--2" />
            <span className="profile-card__blob profile-card__blob--3" />
          </div>
          <p className="profile-card__banner-label">Profile Details</p>
        </div>

        {/* ── Avatar ─────────────────────────────────────────── */}
        <div className="profile-card__avatar-wrap">
          <div className="profile-card__avatar-ring">
            <AppUpload
              avatar
              image={editContent?.image !== "" ? editContent?.image : "/img/new/profile.svg"}
              uploadedImage={uploadedImage}
              handleFileUpload={handleFileUpload}
            />
          </div>
        </div>

        {/* ── Name & company ─────────────────────────────────── */}
        <div className="profile-card__identity">
          <h2 className="profile-card__name">{editContent?.full_name || "Your Name"}</h2>
          <span className="profile-card__company">{editContent?.company || "—"}</span>
        </div>

        {/* ── Form ───────────────────────────────────────────── */}
        <Form name="profile_form" initialValues={{ remember: true }} onFinish={onFinish} className="profile-card__form">

          <p className="profile-card__section-label">Account Details</p>

          <div className="profile-card__field">
            <label className="profile-card__label">
              <span>👤</span> Full Name
            </label>
            <Input
              name="full_name"
              className="profile-card__input"
              value={editContent?.full_name}
              onChange={handleChangeInput}
              type="text"
              placeholder="Enter your full name"
              suffix={<Image src="/img/new/edit_input.svg" alt="" preview={false} width={15} />}
            />
          </div>

          <div className="profile-card__field">
            <label className="profile-card__label">
              <span>✉️</span> Email
              <span className="profile-card__badge">Read-only</span>
            </label>
            <Input
              name="email"
              className="profile-card__input profile-card__input--disabled"
              value={editContent?.email}
              disabled
              type="email"
              placeholder="Enter your email"
              suffix={<Image src="/img/new/edit_input.svg" alt="" preview={false} width={15} />}
            />
          </div>

          <div className="profile-card__field">
            <label className="profile-card__label">
              <span>🏢</span> Company
              <span className="profile-card__badge">Read-only</span>
            </label>
            <Input
              name="company"
              className="profile-card__input profile-card__input--disabled"
              value={editContent?.company}
              disabled
              type="text"
              placeholder="Company name"
              suffix={<Image src="/img/new/edit_input.svg" alt="" preview={false} width={15} />}
            />
          </div>

          <Button
            loading={isLoading}
            disabled={isEditable}
            className="profile-card__save-btn"
            onClick={onFinish}
          >
            Save Changes
          </Button>

          <p className="profile-card__section-label" style={{ marginTop: 24 }}>Security & Address</p>

          <div className="profile-card__action-row">
            <button type="button" className="profile-card__action-btn" onClick={() => setIsVisible(true)}>
              <span className="profile-card__action-icon">🔒</span>
              <span className="profile-card__action-text">
                <strong>Change Password</strong>
                <small>Update your login credentials</small>
              </span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="profile-card__action-arrow">
                <path d="M4 8h8M9 5l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <button type="button" className="profile-card__action-btn" onClick={() => setIsVisibleAddress(true)}>
              <span className="profile-card__action-icon">📍</span>
              <span className="profile-card__action-text">
                <strong>Office Address</strong>
                <small>View or update your address</small>
              </span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="profile-card__action-arrow">
                <path d="M4 8h8M9 5l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

        </Form>
      </div>

      {/* ── Change Password Modal ─────────────────────────────── */}
      <AppModal
        title="Change Password"
        open={visible}
        onCancel={handleCancel}
        className="profile-modal"
      >
        <div className="profile-modal__inner">
          <p className="profile-modal__desc">Enter a strong new password for your account.</p>
          <div className="profile-modal__field">
            <label className="profile-card__label">New Password</label>
            <Input
              className="profile-card__input"
              type="password"
              name="password"
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              suffix={<Image src="/img/new/edit_input.svg" alt="" preview={false} width={15} />}
            />
          </div>
          <Button
            loading={isLoading}
            className="profile-modal__cta"
            onClick={() => {
              if (password === "") message.info("Please fill in a password");
              else updatePassword();
            }}
          >
            Update Password
          </Button>
        </div>
      </AppModal>

      {/* ── Address Modal ─────────────────────────────────────── */}
      <AppModal
        title="Your Office Address"
        open={isVisibleAddress}
        onCancel={handleCancelAddress}
      >
        <AddressPopup showTitle={false} />
      </AppModal>
    </div>
  );
}

export default Profile;